import React, { useEffect, useState } from 'react';
import Phaser from 'phaser';
import LoadingScene from '../scenes/LoadingScene';
import MainScene from '../scenes/MainScene';
import RiverScene from '../scenes/RiverScene';
import BeachScene from '../scenes/BeachScene';
import FarmScene from '../scenes/FarmScene';
import MenuScene from '../scenes/MenuScene';
import MenuBarScene from '../scenes/MenuBarScene';
import InventoryScene from '../scenes/InventoryScene';
import VendorScene from '../scenes/VendorScene';
import Combat1Scene from '../scenes/Combat1Scene';
import CryptoCrashGame from './minigame/CryptoCrash';
import { inventoryManager } from '../managers/InventoryManager';
import { careManager } from '../managers/CareManager';
import useGameStore from '../stores/gameStore';

const Game = ({ onWalletChange, walletAddress, userData }) => {
    const [isMobile, setIsMobile] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [showCryptoCrash, setShowCryptoCrash] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);
    const { initializeUser, clearUser, username, petName } = useGameStore();

    // Check for mobile device
    useEffect(() => {
      const checkMobile = () => {
        setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
        setIsReady(true);
      };
      checkMobile();
    }, []);

    // Initialize user data
    useEffect(() => {
      const initGame = async () => {
        if (walletAddress && userData) {
          setIsInitializing(true);
          try {
            const success = await initializeUser(walletAddress);
            if (!success) {
              console.error('Failed to initialize user data');
              onWalletChange();
              return;
            }
            
            // Initialize care manager after user initialization
            const careSuccess = await careManager.initialize(walletAddress);
            if (!careSuccess) {
              console.warn('Failed to initialize care manager');
            } else {
              // Record login event
              await careManager.recordLogin();
            }
            
          } catch (error) {
            console.error('Error initializing game:', error);
            onWalletChange();
          } finally {
            setIsInitializing(false);
          }
        }
      };
      initGame();
    }, [walletAddress, userData, initializeUser, onWalletChange]);
   
    // Handle wallet changes
    useEffect(() => {
      const handleAccountChange = async (publicKey) => {
        if (!publicKey) {
          clearUser();
          onWalletChange();
        }
      };

      if (window.phantom?.solana) {
        window.phantom.solana.on('accountChanged', handleAccountChange);
      }
      
      return () => {
        if (window.phantom?.solana) {
          window.phantom.solana.off('accountChanged', handleAccountChange);
        }
      };
    }, [onWalletChange, clearUser]);
   
    // Initialize Phaser game
    useEffect(() => {
      if (!isReady || isInitializing) return;

      // Determine zoom level based on screen height
      const getZoomLevel = () => {
        const screenHeight = window.innerHeight;
        if (screenHeight >= 1270) {
          return 3;
        }
        else if (screenHeight >= 910) {
          return 3;
        }  
        return isMobile ? 1.5 : 4;
      };

      const config = {
        type: Phaser.AUTO,
        parent: 'phaser-container',   
        width: 256,
        height: 256,
        zoom: getZoomLevel(),
        pixelArt: true,
        antialias: false,
        scene: [LoadingScene, MainScene, FarmScene, BeachScene, RiverScene, MenuBarScene, MenuScene, InventoryScene, VendorScene, Combat1Scene],
        transparent: true,
        defaultStyle: {
          fontFamily: 'Vhs',
          fontSize: '48px'
        },
        fps: {
          target: 60,
          forceSetTimeOut: false
        },
        physics: {
          default: 'arcade',
          arcade: {
            gravity: { y: 0 },
            debug: false
          }
        },
        render: {
          // Optimize rendering
          pixelArt: true,
          antialias: false,
          roundPixels: true,
          powerPreference: 'high-performance'
      }
      };
    
      const game = new Phaser.Game(config);

      // Make game instance globally available for scenes
      window.game = game;
      
      // Add store data to game for scene access
      game.registry.set('gameStore', useGameStore.getState());
      
      // Subscribe to store changes
      const unsubscribe = useGameStore.subscribe(
        state => game.registry.set('gameStore', state)
      );
    
      // Listen for the global event
      game.events.on('showCryptoCrash', () => {
        setShowCryptoCrash(true);
      });
    
      return () => {
        game.events.off('showCryptoCrash');
        unsubscribe();
        game.destroy(true);
      };
    }, [isReady, isMobile, isInitializing]);
   
    if (isInitializing || !isReady) {
      return (
        <div className="w-full h-screen flex items-center justify-center">
          <div className="text-2xl text-neutral-600">正在加载 Loading...</div>
        </div>
      );
    }
   
    return (
      <div className="relative w-full h-screen flex flex-col items-center gap-2 pt-8 drop-shadow-2xl">
        {/* Background Image */}
        <div
          className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat -z-10"
          style={{ backgroundImage: 'url(/sprites/bg/bg_m_14.png)' }}
        />

        <img src="./binagotchi_logo.png" alt="header" className="w-72"/>
        <div className="text-neutral-600">
          {username && petName ? (
            <>
              <span className="text-xl font-bold text-pinkAccent drop-shadow-[2px_2px_2px_rgba(255,255,255,0.9)]">以...身份登录 Logged in as <span className="font-bold text-white drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]">{username}</span> 与宠物 with pet <span className="font-bold text-white drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]">{petName}</span></span>
            </>
          ) : ''}
        </div>
        <div className="text-yellow-300 text-lg font-bold mt-0">
          CA: 0x14eb13e9f8ccf1b744dd3254d10d3740c4ed4444
        </div>

        {/* Main game area with side panels */}
        <div className="flex gap-6 items-start">
          {/* Left Panel - Controls */}
           <div className="w-80 bg-white/80 backdrop-blur-sm rounded-lg p-4 shadow-lg max-h-[575px] overflow-auto">
              <h3 className="text-lg font-bold text-neutral-800 mb-3 border-b pb-2">农场控制系统 <br/>Farm Controls</h3>
              <div className="space-y-2 text-sm text-neutral-700">
                <div className="flex items-start gap-2">
                  <span className="font-semibold min-w-[80px]">WASD/<br/>Arrow Keys:</span>
                  <span>移动 Move your pet</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold min-w-[80px]">B Key:</span>
                  <span>库存 Inventory</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold min-w-[80px]">T Key:</span>
                  <span>工具菜单 Tool Menu</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold min-w-[80px]">Y Key:</span>
                  <span>种子菜单 Seed menu</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold min-w-[80px]">Left Click:</span>
                  <span>主要工具槽 Primary Tool Slot</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold min-w-[80px]">Right Click:</span>
                  <span>次级工具槽 Secondary Tool Slot</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold min-w-[80px]">Mouse:</span>
                  <span>点击互动 Click to interact</span>
                </div>
              </div>
              <h3 className="text-lg font-bold text-neutral-800 mt-3 mb-3 border-b pb-2">捕鱼控制 Fishing Control</h3>
              <div className="space-y-2 text-sm text-neutral-700">
                <div className="flex items-start gap-2">
                  <span className="font-semibold min-w-[80px]">W/A Key:</span>
                  <span>捕鱼控制 左边和右边 Fishing controls left and right</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold min-w-[80px]">左键点击 Left click:</span>
                  <span>钓组/鱼钩 Cast/Hook</span>
                </div>
              </div>
              <h3 className="text-lg font-bold text-neutral-800 mt-3 mb-3 border-b pb-2">首页 Home</h3>
              <div className="space-y-2 text-sm text-neutral-700">
                <div className="flex items-start gap-2">
                  <span className="font-semibold min-w-[80px]">C Key:</span>
                  <span>加密货币崩盘 Crypto Crash</span>
                </div>
                {/* <div className="flex items-start gap-2">
                  <span className="font-semibold min-w-[80px]">R Key:</span>
                  <span>迁往河畔 Move to River</span>
                </div> */}
                <div className="flex items-start gap-2">
                  <span className="font-semibold min-w-[80px]">F Key:</span>
                  <span>迁往农场 Go to Farm</span>
                </div>
              </div>
          </div>

          {/* Phaser Canvas */}
          <div id="phaser-container" className="bg-transparent drop-shadow-2xl"/>

          {/* Right Panel - Gameplay Loop */}
          <div className="w-64 bg-white/80 backdrop-blur-sm rounded-lg p-4 shadow-lg">
            <h3 className="text-lg font-bold text-neutral-800 mb-3 border-b pb-2">游戏玩法 Gameplay</h3>
            <div className="space-y-3 text-sm text-neutral-700">
              <div>
                <span className="font-semibold block mb-1">1. 关爱您的宠物 Care for your pet</span>
                <p className="text-xs text-neutral-600">喂食、玩耍，让你的宠物开心，赚取护理点数 Feed, play, and keep your pet happy to earn care points /</p>
              </div>
              <div>
                <span className="font-semibold block mb-1">2. 农场与渔业 Farm & Fish</span>
                <p className="text-xs text-neutral-600">种植作物、捕捞鱼类，既能喂养宠物又能赚取积分 Grow crops and catch fish to feed your pet and earn points</p>
              </div>
              <div>
                <span className="font-semibold block mb-1">3. 赚取积分 Earn Points</span>
                <p className="text-xs text-neutral-600">食用农作物（价值为¼）和鱼类（价值为全额）可增加你的得分 Consuming crops (¼ value) and fish (full value) adds to your score</p>
              </div>
              <div>
                <span className="font-semibold block mb-1">4. 空投攀登排行榜 Climb Leaderboard for Airdrop</span>
                <p className="text-xs text-neutral-600">查看你的排名，与其他玩家一较高下！获取空投的$CHI代币。 Check your rank and compete with other players! Get airdropped $CHI.</p>
              </div>
              <div className="pt-2 border-t">
                <span className="font-semibold block mb-1">💡 专业技巧 Pro Tip</span>
                <p className="text-xs text-neutral-600">高价值鱼类可获得更多积分。持续照料能提升你的得分！ High-value fish give more points. Consistent care boosts your score!</p>
              </div>
            </div>
          </div>
        </div>

        {showCryptoCrash && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="relative bg-white rounded-lg">
              <CryptoCrashGame onClose={() => setShowCryptoCrash(false)} />
            </div>
          </div>
        )}
      </div>
    );
};

export default Game;